import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { getFormResponses } from '../../api';

const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14 };

function fmtDeadline(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function formatAnswer(q, answer) {
  if (!answer) return '—';
  if (q.type === 'checkbox') return (answer.answer_json || []).join(', ') || '—';
  if (q.type === 'scale') return answer.answer_json?.value ?? '—';
  return answer.answer_text || '—';
}

function AggregateCard({ question, aggregate }) {
  if (question.type === 'scale') {
    const pct = aggregate.average != null && question.scale_max
      ? Math.max(0, Math.min(100, ((aggregate.average - (question.scale_min || 0)) / (question.scale_max - (question.scale_min || 0))) * 100))
      : 0;
    return (
      <div style={{ ...card, padding: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 10 }}>{question.question_text}</div>
        {aggregate.average == null ? (
          <div style={{ fontSize: 13, color: 'var(--text-dimmed)', fontStyle: 'italic' }}>No responses yet.</div>
        ) : (
          <>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#3b82f6' }}>{aggregate.average.toFixed(1)}</div>
            <div style={{ height: 6, borderRadius: 99, background: 'var(--bg-app)', overflow: 'hidden', marginTop: 8 }}>
              <div style={{ height: '100%', width: `${pct}%`, background: '#3b82f6', borderRadius: 99 }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-dimmed)', marginTop: 4 }}>
              <span>{question.scale_min_label || question.scale_min}</span>
              <span>{question.scale_max_label || question.scale_max}</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>{aggregate.count} response{aggregate.count === 1 ? '' : 's'}</div>
          </>
        )}
      </div>
    );
  }

  if (question.type === 'multiple_choice' || question.type === 'checkbox') {
    const dist = aggregate.distribution || {};
    const total = Object.values(dist).reduce((s, n) => s + n, 0);
    return (
      <div style={{ ...card, padding: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 10 }}>{question.question_text}</div>
        {total === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-dimmed)', fontStyle: 'italic' }}>No responses yet.</div>
        ) : (question.options || Object.keys(dist)).map(opt => {
          const count = dist[opt] || 0;
          const pct = total ? (count / total) * 100 : 0;
          return (
            <div key={opt} style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 3 }}>
                <span>{opt}</span><span>{count}</span>
              </div>
              <div style={{ height: 6, borderRadius: 99, background: 'var(--bg-app)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: '#8b5cf6', borderRadius: 99 }} />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // text / yesno — raw list (yesno also gets a quick tally since it's naturally binary)
  const values = aggregate.values || [];
  const tally = question.type === 'yesno'
    ? values.reduce((acc, v) => { acc[v] = (acc[v] || 0) + 1; return acc; }, {})
    : null;
  return (
    <div style={{ ...card, padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 10 }}>{question.question_text}</div>
      {!values.length ? (
        <div style={{ fontSize: 13, color: 'var(--text-dimmed)', fontStyle: 'italic' }}>No responses yet.</div>
      ) : tally ? (
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {Object.entries(tally).map(([v, n]) => <div key={v}>{v}: {n}</div>)}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 140, overflowY: 'auto' }}>
          {values.map((v, i) => (
            <div key={i} style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '6px 8px', background: 'var(--bg-app)', borderRadius: 6 }}>{v}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FormResponses({ formId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getFormResponses(formId).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [formId]);

  if (loading) return <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-dimmed)' }}>Loading…</div>;
  if (!data) return <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-dimmed)' }}>Form not found.</div>;

  const { form, questions, recipients, aggregates } = data;
  const aggByQ = Object.fromEntries(aggregates.map(a => [a.question_id, a]));

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <button onClick={onBack} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, marginBottom: 16, padding: 0 }}>
        <ArrowLeft size={14} /> Back to forms
      </button>

      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>{form.title}</h1>
        {form.description && <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>{form.description}</p>}
        <p style={{ color: 'var(--text-dimmed)', fontSize: 12, marginTop: 6 }}>Deadline: {fmtDeadline(form.deadline)} · {recipients.filter(r => r.status === 'submitted').length}/{recipients.length} submitted</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 24 }}>
        {questions.map(q => <AggregateCard key={q.id} question={q} aggregate={aggByQ[q.id] || {}} />)}
      </div>

      <div style={{ ...card, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
          <thead>
            <tr>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid var(--border)', background: 'var(--bg-app)', whiteSpace: 'nowrap' }}>Player</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid var(--border)', background: 'var(--bg-app)', whiteSpace: 'nowrap' }}>Status</th>
              {questions.map(q => (
                <th key={q.id} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid var(--border)', background: 'var(--bg-app)' }} title={q.question_text}>
                  {q.question_text.length > 24 ? q.question_text.slice(0, 24) + '…' : q.question_text}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {recipients.map((r, i) => (
              <tr key={r.id} style={{ background: i % 2 !== 0 ? 'var(--bg-app)' : 'transparent', borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '10px 16px', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{r.player_name}</td>
                <td style={{ padding: '10px 16px' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: r.status === 'submitted' ? '#10b981' : '#f59e0b' }}>
                    {r.status === 'submitted' ? '✓ Submitted' : '● Pending'}
                  </span>
                </td>
                {questions.map(q => (
                  <td key={q.id} style={{ padding: '10px 16px', fontSize: 13, color: 'var(--text-secondary)' }}>
                    {formatAnswer(q, r.answers[q.id])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
